// Core Web Vitals reporting (LCP, INP, CLS, FCP, TTFB).
//
// Each metric is fired at most once per page navigation. We fan it out to
// two destinations:
//
//   1. Caller-supplied callback — typically forwards to the existing
//      /manager-api/telemetry → Prometheus pipeline. Pass undefined if
//      Prometheus aggregation isn't wired yet (Sentry-only mode).
//
//   2. Sentry — as a breadcrumb (visible on any subsequent error event)
//      AND as a measurement (queryable in Performance dashboards).
//
// web-vitals's `id` field is stable per page-load so duplicate samples
// (e.g., LCP re-fired after layout shift) can be deduped downstream.

import { onLCP, onINP, onCLS, onFCP, onTTFB, type Metric } from 'web-vitals'
import * as Sentry from '@sentry/vue'

type MetricSink = (metric: Metric) => void

const SENTRY_ENABLED = !!import.meta.env.VITE_SENTRY_DSN

export function initWebVitals(onMetric?: MetricSink): void {
  const send = (metric: Metric) => {
    // 1) External sink (e.g., Prometheus telemetry).
    try {
      onMetric?.(metric)
    } catch {
      // Never let a downstream telemetry bug abort web-vitals reporting.
    }

    // 2) Sentry — only when configured.
    if (SENTRY_ENABLED) {
      try {
        Sentry.addBreadcrumb({
          category: 'web-vital',
          message: `${metric.name}: ${metric.value.toFixed(2)}`,
          level: ratingToLevel(metric.rating),
          data: {
            name: metric.name,
            value: metric.value,
            rating: metric.rating,
            id: metric.id,
            navigationType: metric.navigationType,
          },
        })
        Sentry.setMeasurement(metric.name, metric.value, metricUnit(metric.name))
      } catch {
        // Sentry SDK should never throw, but be defensive.
      }
    }
  }

  onLCP(send)
  onINP(send)
  onCLS(send)
  onFCP(send)
  onTTFB(send)
}

function ratingToLevel(rating: Metric['rating']): 'info' | 'warning' | 'error' {
  switch (rating) {
    case 'good':
      return 'info'
    case 'needs-improvement':
      return 'warning'
    case 'poor':
      return 'error'
    default:
      return 'info'
  }
}

function metricUnit(name: string): 'millisecond' | 'none' {
  // CLS is a unitless layout shift score; everything else is in ms.
  return name === 'CLS' ? 'none' : 'millisecond'
}
